import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeft, GitCompare, History, ListTree, Pencil, RefreshCw, RotateCcw, X } from 'lucide-react';
import { dynamicFieldsApi } from './api';
import ImpactDialog from './ImpactDialog';
import { libraryImpact } from './impact';
import { ENTRY_KIND_LABELS, LibraryDetail, LibraryImpact, LibraryRef, LibraryVersionItem } from './types';
import { useLibraryRef } from './useLibraryRef';
import { Change, ChangeList } from '../components/ChangeList';
import { Chip, timeAgo } from '../components/history';
import { primaryButton, secondaryButton, Skeleton } from '../ui';
import { ROUTES } from '../../../config/links';

const errorText = (e: any, fallback: string) => {
  const detail = e?.response?.data?.detail;
  return (typeof detail === 'string' ? detail : detail?.message) || e?.message || fallback;
};

/** Version history of one dynamic field library: what each version changed, and roll back. */
export default function DynamicFieldsVersionsPage() {
  const ref = useLibraryRef();
  const navigate = useNavigate();
  const [versions, setVersions] = useState<LibraryVersionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<number>();
  const [tab, setTab] = useState<'changes' | 'entries'>('changes');
  const [compare, setCompare] = useState<'live' | 'previous'>('previous');
  const [changes, setChanges] = useState<Change[]>();
  const [detail, setDetail] = useState<LibraryDetail>();
  const [confirming, setConfirming] = useState(false);

  const history = useMemo(() => versions.filter((v) => v.status !== 'draft'), [versions]);
  const draft = versions.find((v) => v.status === 'draft');
  const live = versions.find((v) => v.status === 'published');
  const current = history.find((v) => v.version === selected);

  const load = useCallback((keepSelection = false) => {
    if (!ref) return Promise.resolve();
    setLoading(true);
    return dynamicFieldsApi.versions(ref)
      .then((rows) => {
        setVersions(rows);
        const past = rows.filter((r) => r.status !== 'draft');
        setSelected((prev) => (keepSelection && prev && past.some((r) => r.version === prev) ? prev : (past.find((r) => r.status === 'published') || past[0])?.version));
      })
      .catch((e) => toast.error(errorText(e, 'Failed to load versions')))
      .finally(() => setLoading(false));
  }, [ref]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!ref || selected == null) return;
    setChanges(undefined);
    dynamicFieldsApi.versionDiff(ref, selected, compare).then(setChanges).catch(() => setChanges([]));
  }, [ref, selected, compare]);

  useEffect(() => {
    if (!ref || selected == null || tab !== 'entries') return;
    setDetail(undefined);
    dynamicFieldsApi.version(ref, selected).then(setDetail).catch((e) => toast.error(errorText(e, 'Failed to load entries')));
  }, [ref, selected, tab]);

  if (!ref) return <div className="grid min-h-full place-items-center bg-slate-50 p-8 text-sm text-slate-500">Choose a library from the <button className="ml-1 font-medium text-primary-700 underline" onClick={() => navigate(ROUTES.DYNAMIC_FIELDS)}>dynamic fields list</button>.</div>;

  return (
    <div className="flex min-h-full flex-col bg-slate-50 p-3 md:p-8 lg:h-full lg:min-h-0 lg:overflow-hidden">
      <header className="mb-4 flex shrink-0 flex-col gap-3 border-b border-slate-200 pb-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-primary-600"><History className="h-3.5 w-3.5" /> Version history</div>
          <h1 className="mt-1 text-xl font-semibold tracking-tight text-slate-900">{ref.sensor_type} <span className="text-slate-300">/</span> {ref.category}</h1>
          <p className="mt-1 max-w-2xl text-xs text-slate-500">Something wrong with the live fields? Pick an earlier version, check what differs, and make it live again. Nothing is deleted: the rollback is added as a new version.</p>
        </div>
        <div className="flex gap-2">
          <button className={secondaryButton} onClick={() => load(true)} disabled={loading}><RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh</button>
          <button className={secondaryButton} onClick={() => navigate(ROUTES.dynamicFieldsAudit(ref.sensor_type, ref.category))}><History className="h-3.5 w-3.5" /> Audit log</button>
          <button className={secondaryButton} onClick={() => navigate(ROUTES.dynamicFieldsEdit(ref.sensor_type, ref.category))}><Pencil className="h-3.5 w-3.5" /> Edit</button>
          <button className={secondaryButton} onClick={() => navigate(ROUTES.DYNAMIC_FIELDS)}><ArrowLeft className="h-3.5 w-3.5" /> All libraries</button>
        </div>
      </header>

      <div className="grid gap-4 lg:min-h-0 lg:flex-1 lg:grid-cols-[minmax(280px,360px)_minmax(0,1fr)] lg:grid-rows-[minmax(0,1fr)]">
        <ol className="max-h-80 space-y-2 overflow-y-auto pr-1 lg:max-h-none lg:min-h-0" aria-label="Versions">
          {loading && versions.length === 0 && Array.from({ length: 4 }).map((_, i) => <li key={i}><Skeleton className="h-20 w-full rounded-xl" /></li>)}
          {draft && (
            <li className="rounded-xl border border-dashed border-amber-300 bg-amber-50/60 p-3">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2"><span className="text-[13px] font-semibold text-slate-800">Draft (v{draft.version})</span><Chip cls="bg-amber-100 text-amber-700">Unpublished</Chip></div>
                <button className="inline-flex items-center gap-1 text-[11px] font-medium text-primary-700 hover:underline" onClick={() => navigate(ROUTES.dynamicFieldsEdit(ref.sensor_type, ref.category))}><Pencil className="h-3 w-3" /> Open editor</button>
              </div>
              <p className="mt-1 text-[11px] text-slate-500">Work in progress. Rolling back does not touch your draft.</p>
            </li>
          )}
          {history.map((v) => (
            <li key={v.version}>
              <button type="button" onClick={() => setSelected(v.version)} aria-current={selected === v.version}
                className={`w-full rounded-xl border bg-white p-3 text-left shadow-sm transition hover:border-slate-300 ${selected === v.version ? 'border-primary-400 ring-2 ring-primary-100' : 'border-slate-200'}`}>
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="text-[13px] font-semibold text-slate-900">Version {v.version}</span>
                  {v.status === 'published' ? <Chip cls="bg-emerald-100 text-emerald-700">● Live now</Chip> : <Chip cls="bg-slate-100 text-slate-500">Previous</Chip>}
                  {v.restored_from != null && <Chip cls="bg-violet-100 text-violet-700">Rolled back to v{v.restored_from}</Chip>}
                </div>
                <div className="mt-1 text-[11px] text-slate-500" title={v.published_at ? new Date(v.published_at).toLocaleString() : undefined}>
                  {v.published_at ? `${timeAgo(v.published_at)} · ${new Date(v.published_at).toLocaleDateString()}` : 'Publish date unknown'}
                  {v.published_by_name ? ` · by ${v.published_by_name}` : ''}
                </div>
                {v.note && <p className="mt-1.5 line-clamp-2 text-[12px] italic text-slate-600">“{v.note}”</p>}
              </button>
            </li>
          ))}
          {!loading && history.length === 0 && <li className="rounded-xl border border-slate-200 bg-white p-6 text-center text-[12px] text-slate-400">This library has not been published yet.</li>}
        </ol>

        <section className="flex min-w-0 flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm lg:min-h-0" aria-label="Version details">
          {!current ? (
            <div className="grid min-h-[200px] place-items-center p-8 text-[12px] text-slate-400">Select a version to see what it contains.</div>
          ) : (
            <>
              <div className="flex shrink-0 flex-col gap-3 border-b border-slate-100 p-4 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <h2 className="text-sm font-semibold text-slate-900">Version {current.version}{current.status === 'published' ? ' — currently live' : ''}</h2>
                  <p className="mt-0.5 text-[11px] text-slate-500">{current.published_by_name ? `Published by ${current.published_by_name}` : 'Published'}{current.published_at ? ` on ${new Date(current.published_at).toLocaleString()}` : ''}</p>
                  {current.note && <p className="mt-1.5 text-[12px] italic text-slate-600">“{current.note}”</p>}
                </div>
                {current.status !== 'published' && live && (
                  <button className={primaryButton} onClick={() => setConfirming(true)}><RotateCcw className="h-3.5 w-3.5" /> Make this version live</button>
                )}
              </div>
              <div className="flex shrink-0 items-center justify-between gap-2 border-b border-slate-100 px-4 pt-2">
                <div role="tablist" className="flex gap-1">
                  {([['changes', 'What\'s different', GitCompare], ['entries', 'Entries', ListTree]] as const).map(([key, label, Icon]) => (
                    <button key={key} role="tab" aria-selected={tab === key} onClick={() => setTab(key)}
                      className={`inline-flex items-center gap-1.5 border-b-2 px-3 py-2 text-[12px] font-medium ${tab === key ? 'border-primary-600 text-primary-700' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>
                      <Icon className="h-3.5 w-3.5" /> {label}
                    </button>
                  ))}
                </div>
                {tab === 'changes' && (
                  <label className="flex items-center gap-1.5 pb-1 text-[11px] text-slate-500">Compare with
                    <select value={compare} onChange={(e) => setCompare(e.target.value as 'live' | 'previous')} className="rounded-md border border-slate-200 bg-white px-1.5 py-1 text-[11px] text-slate-700">
                      <option value="previous">Previous version</option>
                      <option value="live">Live version{live ? ` (v${live.version})` : ''}</option>
                    </select>
                  </label>
                )}
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto p-4">
                {tab === 'changes' && (
                  changes === undefined ? <Skeleton className="h-24 w-full" /> :
                  changes.length === 0 ? <p className="text-[12px] text-slate-500">{current.status === 'published' && compare === 'live' ? 'This is the live version.' : 'No differences.'}</p> :
                  <>
                    <p className="text-[11px] text-slate-500">{compare === 'live' ? `Changes users would get if v${current.version} replaced the live fields:` : `Changes v${current.version} made compared with the previous version:`}</p>
                    <ChangeList changes={changes} />
                  </>
                )}
                {tab === 'entries' && (
                  !detail ? <Skeleton className="h-40 w-full" /> : (
                    <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
                      {detail.fields.map((f) => (
                        <li key={f.key} className="flex items-center gap-3 px-3 py-2 text-[12px]">
                          <span className={`min-w-0 flex-1 truncate font-medium ${f.is_active ? 'text-slate-800' : 'text-slate-400 line-through'}`}>{f.label}</span>
                          <code className="text-[10px] text-slate-500">{f.key}</code>
                          <span className="text-[10px] text-slate-400">{ENTRY_KIND_LABELS[f.kind]}</span>
                        </li>
                      ))}
                      {detail.fields.length === 0 && <li className="p-4 text-center text-[12px] text-slate-400">No entries.</li>}
                    </ul>
                  )
                )}
              </div>
            </>
          )}
        </section>
      </div>

      {confirming && current && live && (
        <RollbackDialog refKey={ref} target={current} live={live} nextVersion={Math.max(...versions.map((v) => v.version)) + 1} onClose={() => setConfirming(false)}
          onDone={() => { setConfirming(false); setCompare('previous'); load(); }} />
      )}
    </div>
  );
}

function RollbackDialog({ refKey, target, live, nextVersion, onClose, onDone }: { refKey: LibraryRef; target: LibraryVersionItem; live: LibraryVersionItem; nextVersion: number; onClose: () => void; onDone: () => void }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [changes, setChanges] = useState<Change[]>();
  const [impact, setImpact] = useState<LibraryImpact>();

  useEffect(() => { dynamicFieldsApi.versionDiff(refKey, target.version, 'live').then(setChanges).catch(() => setChanges([])); }, [refKey, target.version]);

  const confirm = async (acknowledgeImpact = false) => {
    setBusy(true);
    try {
      const restored = await dynamicFieldsApi.rollback(refKey, target.version, reason.trim(), acknowledgeImpact);
      toast.success(`Version ${target.version} is live again (saved as v${restored.version})`);
      onDone();
    } catch (e) {
      // The older version lacks entries that published forms select: ask before going ahead.
      const found = libraryImpact(e);
      if (found) setImpact(found);
      else { setImpact(undefined); toast.error(errorText(e, 'Rollback failed')); }
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/40 p-4" role="dialog" aria-modal="true" aria-labelledby="library-rollback-title">
      <div className="flex max-h-[90vh] w-full max-w-xl flex-col rounded-2xl bg-white shadow-xl">
        <div className="flex items-start justify-between gap-3 border-b border-slate-100 p-4">
          <div>
            <h2 id="library-rollback-title" className="text-base font-semibold text-slate-900">Make version {target.version} the live library?</h2>
            <p className="mt-0.5 text-[12px] text-slate-500">Version {live.version} is live now. Published forms that select these entries will use version {target.version} straight away.</p>
          </div>
          <button className="rounded-md p-1 text-slate-400 hover:bg-slate-100" onClick={onClose} aria-label="Close"><X className="h-4 w-4" /></button>
        </div>
        <div className="space-y-3 overflow-y-auto p-4">
          <ul className="space-y-1 rounded-lg bg-slate-50 p-3 text-[12px] text-slate-600">
            <li>• A new version <strong>v{nextVersion}</strong> is created with the same content as v{target.version}.</li>
            <li>• Version {live.version} and all other versions stay in history, so you can switch back.</li>
            <li>• Your current draft is <strong>not</strong> affected.</li>
          </ul>
          <div>
            <div className="text-[12px] font-medium text-slate-700">What will change for users</div>
            {changes === undefined ? <Skeleton className="mt-2 h-16 w-full" /> : changes.length === 0 ? <p className="mt-1 text-[12px] text-slate-500">No visible differences.</p> : <ChangeList changes={changes} />}
          </div>
          <label className="block text-[12px] font-medium text-slate-700">Why are you rolling back? <span className="font-normal text-slate-400">(recorded in history)</span>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} maxLength={1000} rows={2} placeholder="e.g. The temperature rows were removed by mistake"
              className="mt-1 w-full rounded-lg border border-slate-200 p-2 text-[12px] outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100" />
          </label>
        </div>
        <div className="flex justify-end gap-2 border-t border-slate-100 p-4">
          <button className={secondaryButton} onClick={onClose} disabled={busy}>Cancel</button>
          <button className={primaryButton} onClick={() => confirm()} disabled={busy}><RotateCcw className="h-3.5 w-3.5" /> {busy ? 'Rolling back…' : `Roll back to v${target.version}`}</button>
        </div>
      </div>
      {impact && (
        <ImpactDialog impact={impact} confirmLabel={`Roll back to v${target.version} anyway`} busy={busy} onConfirm={() => confirm(true)} onCancel={() => setImpact(undefined)} />
      )}
    </div>
  );
}
